/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Paper,
  useTheme,
} from "@mui/material";
import * as React from "react";

import {
  RobotoButton,
  RobotoIcon,
  RobotoTypography,
} from "@/shared/components";
import { OrgInviteRecord } from "@/shared/domain/orgs";
import { useAPICall, useLazyAPICall } from "@/shared/services/apiHooks";
import { GetInviteResponse } from "@/shared/services/ApiService";
import {
  acceptInviteEndpoint,
  declineInviteEndpoint,
  inviteEndpoint,
} from "@/types";

interface InviteResponseFormProps {
  inviteId: string | undefined;
  onAccepted: (invite: OrgInviteRecord) => void;
  onDeclined: (invite: OrgInviteRecord) => void;
  onMissing: () => void;
}

export const InviteResponseForm: React.FC<InviteResponseFormProps> = ({
  inviteId,
  onAccepted,
  onDeclined,
  onMissing,
}) => {
  const theme = useTheme();

  const [firstLoad, setFirstLoad] = React.useState<boolean>(true);

  const { data, loading, error } = useAPICall<GetInviteResponse>({
    endpoint: inviteEndpoint,
    method: "GET",
    pathParams: { inviteId: inviteId ?? "" },
  });

  React.useEffect(() => {
    if ((data || error) && !loading) {
      setFirstLoad(false);
    }

    if (error?.errorCode === "RobotoNotFoundException") {
      onMissing();
    }
  }, [data, error, loading, onMissing]);

  const [acceptDeclineErrorMsg, setAcceptDeclineErrorMsg] =
    React.useState<string>("");

  const { initiateRequest } = useLazyAPICall();

  const onAcceptedWrapper = async () => {
    const { error } = await initiateRequest({
      endpoint: acceptInviteEndpoint,
      method: "POST",
      pathParams: { inviteId: inviteId ?? "" },
    });

    if (error) {
      setAcceptDeclineErrorMsg(error.message);
      return;
    }

    onAccepted(data?.data as OrgInviteRecord);
  };

  const onDeclinedWrapper = async () => {
    const { error } = await initiateRequest({
      endpoint: declineInviteEndpoint,
      method: "POST",
      pathParams: { inviteId: inviteId ?? "" },
    });

    if (error) {
      setAcceptDeclineErrorMsg(error.message);
      return;
    }

    onDeclined(data?.data as OrgInviteRecord);
  };

  if (error || acceptDeclineErrorMsg) {
    return (
      <Container
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper sx={{ padding: theme.spacing(2), marginTop: theme.spacing(2) }}>
          <RobotoIcon />
          {error && (
            <RobotoTypography
              variant={"body2"}
              sx={{ color: theme.palette.error.main }}
            >
              Error: {error.message}
            </RobotoTypography>
          )}
          {acceptDeclineErrorMsg && (
            <RobotoTypography
              variant={"body2"}
              sx={{ color: theme.palette.error.main }}
            >
              Error: {acceptDeclineErrorMsg}
            </RobotoTypography>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Container
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <Paper
        sx={{
          padding: theme.spacing(3),
          marginTop: theme.spacing(2),
          minWidth: "400px",
        }}
      >
        <RobotoIcon />
        <RobotoTypography
          variant={"h6"}
          sx={{
            marginBottom: theme.spacing(2),
            display: "flex",
            justifyContent: "center",
            fontWeight: 500,
          }}
        >
          Join&nbsp;
          <span style={{ fontWeight: 700 }}>{data?.data.org.name}</span>
          &nbsp;on&nbsp;
          <span style={{ fontWeight: 700 }}>Roboto</span>
        </RobotoTypography>
        <RobotoTypography
          sx={{
            marginBottom: theme.spacing(2),
            display: "flex",
            justifyContent: "center",
          }}
        >
          You were invited by:
        </RobotoTypography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: theme.spacing(2),
          }}
        >
          <Avatar
            src={data?.data?.invited_by?.picture_url}
            sx={{ width: 30, height: 30, marginRight: theme.spacing(1.5) }}
            imgProps={{ referrerPolicy: "no-referrer" }}
          />

          <RobotoTypography>{data?.data.invited_by.user_id}</RobotoTypography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            mt: theme.spacing(4),
            mb: theme.spacing(1),
          }}
        >
          {loading || (firstLoad && <CircularProgress size="1.75rem" />)}

          {!loading && !firstLoad && (
            <>
              <RobotoButton
                eventName={"AcceptOrgInviteClicked"}
                eventProperties={{ inviteId: inviteId ?? "" }}
                onClick={() => {
                  void onAcceptedWrapper();
                }}
                variant={"contained"}
                sx={{ width: "150px", marginRight: theme.spacing(2) }}
              >
                Accept
              </RobotoButton>
              <RobotoButton
                eventName={"DeclineOrgInviteClicked"}
                eventProperties={{ inviteId: inviteId ?? "" }}
                onClick={() => {
                  void onDeclinedWrapper();
                }}
                variant={"outlined"}
                sx={{ width: "150px" }}
              >
                Decline
              </RobotoButton>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};
